import { useCallback } from 'react';
import { collection, CollectionReference, DocumentReference } from 'firebase/firestore';
import type { Novel } from '@/lib/types';
import type { NovelDao } from '@/api/daos/novel-dao';

type ValidFamily = 'entities' | 'narrative';

export function useNovelRefs(novel: Novel, novelDao: NovelDao) {
  const getItemRef = useCallback(
    (family: ValidFamily, type: string, id: string): DocumentReference | null => {
      if (!novel.id) return null;
      return novelDao.resolver.resolveItem(novel.ownerId, novel.id, family, type, id, novel.storageLayout).ref;
    },
    [novel, novelDao]
  );

  const getCollectionRef = useCallback(
    (family: ValidFamily, type: string): CollectionReference | null => {
      if (!novel.id) return null;
      const layout = (family === 'entities' ? novel.storageLayout.entities : novel.storageLayout.narrative)[type]?.items || 'embedded';
      if (layout === 'split') {
        const root = novelDao.resolver.getNovelRef(novel.ownerId, novel.id);
        return collection(root, type);
      }
      return null;
    },
    [novel, novelDao]
  );

  const getBucketRef = useCallback(
    (family: ValidFamily, type: string): DocumentReference | null => {
      if (!novel.id) return null;
      return novelDao.resolver.getBucketRef(novel.ownerId, novel.id, family, type);
    },
    [novel, novelDao]
  );

  return { getItemRef, getCollectionRef, getBucketRef };
}
